<template>
  <Loader :value="promises.fetchOne" :message="$metaInfo.title" class="user-mutate">
    <div class="container view">
      <HeaderComponent
        :title="$metaInfo.title"
        :breadcrumb="[{ title: labels.users, action: { name: 'users' } }, { title: $metaInfo.title }]"
      >
        <template #right>
          <div class="row wrap g-1">
            <Actions placement="user" :query="{ user: id }">
              <template #bottom>
                <template v-if="!isNew && $store.getters['isAdmin']">
                  <ButtonComponent
                    v-if="!item.isConfirmed"
                    :label="labels.reinvite"
                    :icon="['fal', 'envelope']"
                    :promise="reconfirmPromise"
                    @click.native="reconfirm"
                  />
                  <ButtonComponent
                    v-if="
                      ($store.getters['isSuperadmin'] || $store.getters['isOwner']) &&
                      data.access.includes('admin') &&
                      !data.access.includes('owner')
                    "
                    :label="$t('assignAccountOwner')"
                    confirm
                    @click="setOwner"
                    icon="fa-pro-light:crown"
                  />
                  <ButtonComponent
                    v-if="!data.access.includes('owner')"
                    :label="labels.remove"
                    @click="removeDialog = true"
                    :icon="['fal', 'times']"
                  />
                </template>
              </template>
            </Actions>
          </div>
        </template>
      </HeaderComponent>
      <Card tag="form" @submit.prevent="submit" class="user-mutate__form">
        <template #header>
          <h2 class="user-mutate__title">
            {{ $t('aboutUser') }}
            <div v-if="data.access.includes('owner')" class="user-mutate__title-owner">{{ $t('accountOwner') }}</div>
          </h2>
          <div class="user-mutate__about">
            <InputComponent
              v-model="data.name"
              data-field="name"
              :label="labels.name"
              required
              :is-valid="validator.fields['name'].valid"
              :validation-message="validator.fields['name'].error"
            />

            <InputComponent
              v-model="data.title"
              data-field="title"
              :label="labels.title"
              :is-valid="validator.fields['title'].valid"
              :validation-message="validator.fields['title'].error"
            />

            <InputComponent
              v-model="data.email"
              data-field="email"
              :label="labels.email"
              type="email"
              required
              :disabled="!isNew"
              :is-valid="validator.fields['email'].valid"
              :validation-message="validator.fields['email'].error"
              :icon="['fal', 'envelope']"
            />

            <InputComponent
              v-model="data.phone"
              data-field="phone"
              :label="labels.phone"
              type="tel"
              :is-valid="validator.fields['phone'].valid"
              :validation-message="validator.fields['phone'].error"
              :icon="['fal', 'mobile']"
            />
            <Tags
              data-field="tags"
              :label="$tc('tag', 2)"
              v-model="data.tags"
              :placeholder="$tc('add')"
              autocomplete="/api/user/tags"
            />
          </div>
          <MediaComponent
            v-model="avatarComp"
            :upload-options="{ compression: { maxWidth: 700 } }"
            placement="inside"
            class="user-mutate__logo"
            :description="false"
          >
            <template #drop-message>
              <FontAwesomeIcon :icon="['fal', 'camera-alt']" size="lg" />
              <div class="mt-1">{{ labels.avatar }}</div>
            </template>

            <template #bottom>
              <MediaRecommendation value="user" />
            </template>
          </MediaComponent>

          <div v-if="!isKvass" class="user-mutate__company-info col">
            <hr />
            <CheckboxComponent v-model="hasCompanyInfo" :label="$t('addCompanyInfo')" data-field="hasCompanyInfo" />
            <Company
              v-if="showCompany"
              v-model="data.company"
              :validator="validator"
              validator-prefix="company"
              data-field="company"
              enable-logo
            />
          </div>
        </template>
        <template #default>
          <Access v-if="$store.getters['isAdmin']" v-model="data.access" data-field="access">
            <template #label>
              <h2>{{ $t('userRightsAndAccess') }}</h2>
            </template>
            <template #right>
              <Language v-model="data.language" data-field="language" />
            </template>
          </Access>

          <Roles v-if="$store.getters['isAdmin']" v-model="data.roles" :access="data.access" data-field="roles" />
          <AlertComponent
            v-if="isExternal && isNew && !data.access.includes('guest')"
            class="user-mutate__external-warning"
            theme="warning"
          >
            <span
              v-html="
                $t('externalUserAlert', {
                  domain,
                  email: `<strong>${data.email}</strong>`,
                  value: `<strong>${externalAlert}</strong>`,
                }).trim()
              "
            ></span>

            <CheckboxComponent v-model="externalConfirm" :label="$t('confirm')" />
          </AlertComponent>
        </template>
        <template #footer>
          <ButtonComponent
            :label="isNew ? labels.createUser : labels.save"
            theme="primary"
            type="submit"
            :disabled="!isValid"
            :icon="['fal', isNew ? 'plus' : 'arrow-to-bottom']"
            :promise="promises.mutate"
          />
        </template>
      </Card>

      <RemoveComponent
        :show.sync="removeDialog"
        :title="item.name"
        :action="() => remove({ id, transferTo: transferTo.id })"
        @onSuccess="onRemoved"
        :resource="$tc('user')"
      >
        <InputComponent :label="$t('transferToUser')">
          <UserSelect
            v-model="transferTo"
            search
            required
            :items="fetchUsers"
            :title="$t('selectx', { x: $tc('user').toLowerCase() })"
            class="elder-input__element"
          />
        </InputComponent>
      </RemoveComponent>
    </div>
  </Loader>
</template>

<script>
import Validator from '@/mixins/validator-v2'
import ModelMixin from '@/mixins/Model'
import { mapState, mapActions, mapMutations } from 'vuex'
import { mapi18n } from '@/i18n'
import { Initials } from '@/utils'
import UserApi from '@/api/User'
import CompanyModel from '@/models/Company'
import Store from '@/store'

import { InputComponent } from 'vue-elder-input'
import { DropdownComponent } from 'vue-elder-dropdown'
import { AlertComponent } from 'vue-elder-alert'
import { CheckboxComponent } from 'vue-elder-checkbox'
import Roles from '@/components/User/Roles'
import Access from '@/components/User/Access'
import Language from '@/components/User/Language'
import ChecklistComponent from '@/components/Select/Checklist'
import UserSelect from '@/components/User/Select/Modal'
import Actions from '@/components/Actions'
import HeaderComponent from '@/components/Header'
import RemoveComponent from '@/components/Remove'
import Card from '@/components/Card'
import { MediaComponent } from '@kvass/media'
import MediaRecommendation from '@/components/MediaRecommendation'
import Company from '@/components/Company'
import Tags from '@/components/Tags'

const defaultLanguage = Store.state.enums.Region.language === 'nb' ? 'nb' : 'en'

const ExtractIds = v => (v || []).map(e => e.id || e)
const isExternal = email => {
  return email && !email.endsWith(Store.state.domain)
}
const isKvass = email => {
  const pattern = new RegExp(/@?((kvass\.no|pulsecom\.no)$).*/gi)
  if (!email) return

  return email.match(pattern)
}
const RolesLabels = v => {
  switch (v) {
    case 'manager':
      return 'projectmanager'
    case 'salesman':
      return 'customermanager'
    default:
      return v
  }
}

export default {
  mixins: [
    Validator({
      rules: 'rules',
      labels: 'labels',
      data: 'data',
    }),
    ModelMixin({
      source: 'item',
      model: {
        id: null,
        name: null,
        title: null,
        email: null,
        phone: null,
        avatar: null,
        language: defaultLanguage,
        company: null,
        access: [],
        tags: [],
        roles: {
          project: {
            manager: [],
            salesman: [],
            signingAuthority: [],
            shop: {
              manager: [],
              salesman: [],
              signingAuthority: [],
            },
            complaint: {
              manager: [],
            },
          },
          page: {
            manager: [],
            salesman: [],
          },
        },
      },
      preTransform: {
        'roles.project.manager': ExtractIds,
        'roles.project.salesman': ExtractIds,
        'roles.project.signingAuthority': ExtractIds,
        'roles.project.shop.manager': ExtractIds,
        'roles.project.shop.salesman': ExtractIds,
        'roles.project.shop.signingAuthority': ExtractIds,
        'roles.project.complaint.manager': ExtractIds,
        'roles.page.manager': ExtractIds,
        'roles.page.salesman': ExtractIds,
        access(v) {
          return v.filter(k => !Store.state.enums.VirtualAccesses.includes(k))
        },
      },
      transform: {
        company(val, { email }) {
          return isKvass(email) ? null : val
        },
      },
      guard: true,
    }),
  ],
  props: {
    id: String,
  },
  data() {
    return {
      externalConfirm: false,
      reconfirmPromise: null,
      removeDialog: null,
      transferTo: null,
    }
  },
  watch: {
    id: {
      handler(id) {
        if (!id) return this.set(null)
        return this.fetch({ id })
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState('User', ['item', 'promises']),
    ...mapState(['domain']),
    ...mapi18n('labels', {
      email: 'email',
      name: 'name',
      title: 'title',
      phone: 'phone',
      avatar: 'avatar',
      logo: 'logo',
      'company.name': 'companyName',
      'company.organizationNumber': 'orgNo',
      isGuest: 'userIsGuest',
      isDeveloper: 'userIsDeveloper',
      isOwner: 'userIsOwner',
      save: 'save',
      create: 'create',
      reinvite: 'reinvite',
      remove: 'remove',
      users: ['user', 2],
      createUser: 'createUser',
      loading: 'loading',
      loadingUser() {
        return this.$t('loading', { resource: this.$tc('user', 1).toLowerCase() }) + '...'
      },
    }),

    isNew() {
      return !this.id
    },
    rules() {
      let base = {
        name: 'required',
        email: 'required|email' + (this.isNew ? '|emailAvailable:user' : ''),
        phone: 'mobile',
        title: 'string',
      }

      if (this.data && Boolean(this.data.company)) {
        base = {
          ...base,
          'company.name': 'required',
          'company.organizationNumber': 'required',
        }
      }

      return base
    },
    owner: {
      get() {
        return this.data.access.includes('owner')
      },
      set() {
        const hasOwnerAccess = this.data.access.includes('owner')
        if (!hasOwnerAccess) this.data.access.push('owner')
        else if (hasOwnerAccess && !this.$store.getters.isOwner)
          this.data.access = this.data.access.filter(e => e !== 'owner')
        // TODO: Prevent superAdmin from disabling owner access when there is no other owner
      },
    },
    avatarComp: {
      get() {
        if (!this.data.avatar) return
        return {
          url: this.data.avatar,
          type: 'image/jpeg',
        }
      },
      set(newValue) {
        return (this.data.avatar = newValue
          ? newValue.url
          : `${location.origin}/api/avatar/${this.getInitials(this.data.name)}`)
      },
    },
    hasCompanyInfo: {
      get() {
        return Boolean(this.data.company)
      },
      set(val) {
        this.$set(this.data, 'company', val ? CompanyModel() : null)
      },
    },
    isExternal() {
      return this.data && isExternal(this.data.email)
    },

    isKvass() {
      return this.data && isKvass(this.data.email)
    },
    showCompany() {
      return Boolean(this.data.company)
    },
    addedRoles() {
      let converter = value =>
        value.map(e => {
          return ` ${this.$tc(RolesLabels(e), 1).toLowerCase()}`
        })

      return [
        {
          label: 'project',
          key: 'project',
          value: Object.keys(this.data.roles.project).filter(e => this.data.roles.project[e].length),
        },
        {
          label: 'shopShort',
          key: 'shop',
          value: Object.keys(this.data.roles.project.shop).filter(e => this.data.roles.project.shop[e].length),
        },
      ]
        .filter(i => i.value.length)
        .map(i => {
          return {
            externalLabel: `${'\n'}${this.$tc(i.label, 1)} - ${converter(i.value)}`,
            ...i,
          }
        })
    },
    externalAlert() {
      if (!this.data.access || (!this.data.access.length && !this.addedRoles.length))
        return this.$t('externalUserAlertNoRights')

      if (this.data.access.includes('admin')) return this.$t('externalUserAlertAdmin')

      return `<hr> ${
        this.data.access.length
          ? this.$t('externalUserAlertAccess', {
              access: this.data.access
                .map(i => {
                  let label = ''
                  switch (i) {
                    case 'developer':
                      label = 'devtools'
                      break
                    case 'owner':
                      label = i
                      break
                    default:
                      label = i.slice(0, -1)
                      break
                  }
                  return ` ${this.$tc(label, 2).toLowerCase()}`
                })
                .filter(Boolean)
                .join(),
            })
          : ''
      }  ${this.data.access.length && this.addedRoles.length ? '\n\n' : ''} ${
        this.addedRoles.length
          ? this.$t('externalUserAlertRoles', {
              roles: this.addedRoles.map(i => i.externalLabel).join(),
            })
          : ''
      }`
    },
    isValid() {
      let formValid = this.validator.passes
      let externalValid =
        this.data.access.includes('guest') ||
        !this.isNew ||
        !this.isExternal ||
        (this.isExternal && this.externalConfirm)

      return formValid && externalValid
    },
    isSelf() {
      return this.id === this.$store.state.user.id
    },
  },
  methods: {
    ...mapActions('User', ['fetch', 'mutate', 'remove']),
    ...mapMutations('User', ['set']),
    submit() {
      let data = this.getData()
      if (!this.isNew) delete data.email
      this.mutate(data).then(data => {
        if (!this.isNew) return
        if (this.$route.query.returnTo) return this.$router.push(this.$route.query.returnTo)
        this.$router.replace({ name: 'user-edit', params: { id: data.id } })
      })
    },
    async setOwner() {
      this.data.access.push('owner')
      await this.submit()
      window.location.reload()
    },
    onRemoved() {
      this.resetData()
      this.$router.push({
        name: 'users',
      })
    },

    getInitials: Initials,
    reconfirm() {
      this.reconfirmPromise = UserApi.reconfirm({ id: this.item.id })
    },
    fetchUsers(filter) {
      return UserApi.get(
        {
          filter: {
            ...filter,
            exclude: [this.id],
          },
        },
        { query: 'UserSearch' },
      )
    },
  },
  created() {
    if (this.isNew && this.$auth.isDemo && !this.$auth.user.email.endsWith('@kvass.no'))
      return this.$router.replace({ name: 'users' })
  },
  metaInfo() {
    return {
      title: this.isNew ? this.labels.createUser : this.item.name || this.labels.loadingUser,
    }
  },
  components: {
    MediaComponent,
    InputComponent,
    HeaderComponent,
    DropdownComponent,
    RemoveComponent,
    InputComponent,
    AlertComponent,
    UserSelect,
    CheckboxComponent,
    ChecklistComponent,
    Card,
    MediaRecommendation,
    Actions,
    Roles,
    Access,
    Language,
    Company,
    Tags,
  },
}
</script>

<style lang="scss">
.user-mutate {
  background-color: $background-grey;
  $space-inline: 5rem;
  $space-block: 2.5rem;
  $col-gap: 2rem;
  $row-gap: 0.75rem;
  $space: 1.5rem;

  &__form {
    margin: 0 auto;
    max-width: 1100px;
    --card-spacing: 0 !important;

    display: flex;
    flex-direction: column;
    gap: $space;
    padding-block-end: $space-block;

    h2 {
      line-height: 1;
    }

    .card {
      &__header {
        padding-inline: $space-inline;
        padding-block-start: $space-block;

        display: grid;
        align-items: start;
        gap: $space $col-gap;
        grid-template-columns: repeat(2, 1fr);
        grid-template-areas:
          'title title'
          'about media'
          'companyInfo companyInfo';
      }

      &__content {
        --card-spacing: 0 !important;
        padding-inline: $space-inline;

        display: flex;
        flex-direction: column;
        gap: $space $col-gap;
      }
      &__footer-content {
        justify-content: center;
      }
    }
  }

  &__title {
    grid-area: title;
    margin-bottom: 0;

    &-owner {
      font-weight: normal;
      opacity: 0.6;
      display: inline;
      margin-left: 0.3rem;
      &:before {
        content: '—';
        margin-right: 0.5rem;
      }
    }
  }
  &__about {
    grid-area: about;
    display: flex;
    flex-direction: column;
    gap: $row-gap;
  }

  &__logo {
    grid-area: media;
    align-items: center;

    .kvass-media-droparea {
      $size: 250px;
      height: $size;
      width: $size;
      border-style: solid;
      border-radius: 50%;
      margin-bottom: 0.5rem;
    }
    .media-recommendation {
      margin: $row-gap;
    }
  }

  &__company-info {
    grid-area: companyInfo;
    padding-block-end: $space;
    .company {
      margin-top: $space;
      gap: $row-gap $col-gap;
    }

    hr {
      width: 100%;
      border-color: var(--vue-elder-border-color);
      margin: 0;
      padding-block-end: $space;
    }
  }

  &__owner-access {
    margin-bottom: $space;
  }

  &__external-warning {
    width: 100%;
    white-space: pre-line;

    .elder-checkbox {
      margin-top: $space;

      &__box {
        border-color: var(--warning);
      }
    }
  }

  &__scroller {
    max-height: 350px;
    overflow-y: scroll;

    @include scrollShadow();
  }

  [data-field='language'] {
    border: 1px solid var(--vue-elder-border-color);
    border-radius: var(--vue-elder-border-radius, 3px);
    padding: $space;
  }

  .elder-dropdown__content {
    min-width: 150px;
  }
}
</style>
